import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Hls from 'hls.js';
import './Boclips.css';
import boclipsLogo from '../../assets/boclips.png';
import mdsLogo from '../../assets/Logo.svg';

// ─── Filter configuration ────────────────────────────────────────────────────

const SUBJECT_CATEGORIES = [
  { name: 'English Language Arts', subjects: ['Reading', 'Writing', 'Grammar', 'Literature', 'Vocabulary'] },
  { name: 'Social Studies', subjects: ['History', 'Geography', 'Government', 'Economics', 'Civics'] },
  { name: 'Mathematics', subjects: ['Algebra', 'Geometry', 'Calculus', 'Statistics', 'Arithmetic'] },
  { name: 'Science', subjects: ['Biology', 'Chemistry', 'Physics', 'Earth Science', 'Environmental Science'] },
  { name: 'The Arts', subjects: ['Visual Arts', 'Music', 'Drama', 'Dance', 'Film'] },
  { name: 'Health & PE', subjects: ['Physical Education', 'Health', 'Nutrition', 'Mental Health'] },
];

const BEST_FOR_OPTIONS = [
  { id: 'explainer', label: 'Explainer' },
  { id: 'application', label: 'Application' },
  { id: 'tutorial', label: 'Tutorial' },
  { id: 'discovery', label: 'Discovery' },
  { id: 'experience', label: 'Experience' },
];

const TABS = [
  { id: 'videos', label: 'All videos' },
];

// ─── Inline SVG icons ─────────────────────────────────────────────────────────

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const GridIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </svg>
);

const ListIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <rect x="3" y="4" width="18" height="2.5" rx="1" />
    <rect x="3" y="10.75" width="18" height="2.5" rx="1" />
    <rect x="3" y="17.5" width="18" height="2.5" rx="1" />
  </svg>
);

const ChevronIcon = ({ expanded }) => (
  <svg
    width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s ease' }}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const PlayIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="white" style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.4))' }}>
    <path d="M8 5v14l11-7z" />
  </svg>
);

const CloseIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const ShareIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const LinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDuration = (duration) => {
  if (!duration) return null;
  if (typeof duration === 'number') {
    const m = Math.floor(duration / 60);
    const s = duration % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }
  if (typeof duration === 'string') {
    if (duration.startsWith('PT')) {
      const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
      if (match) {
        const h = parseInt(match[1] || 0);
        const min = parseInt(match[2] || 0);
        const sec = parseInt(match[3] || 0);
        if (h > 0) return `${h}:${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
        return `${min}:${String(sec).padStart(2, '0')}`;
      }
    }
    return duration;
  }
  return null;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

// Walks through various field locations Boclips might put thumbnail URLs
const getVideoThumbnail = (video) => {
  if (!video) return null;

  const directFields = ['thumbnail', 'thumbnailUrl', 'thumbnail_url', 'image', 'poster', 'posterImage', 'coverImage', 'cover'];
  for (const field of directFields) {
    if (video[field]) return video[field];
  }

  // playback._links.thumbnail (primary Boclips structure)
  const pbThumb = video.playback?._links?.thumbnail;
  if (pbThumb?.href) {
    if (pbThumb.templated && pbThumb.href.includes('{thumbnailWidth}')) {
      return pbThumb.href.replace('{thumbnailWidth}', '640');
    }
    return pbThumb.href;
  }

  // Top-level _links (HATEOAS fallback)
  if (video._links) {
    const linkTypes = ['thumbnail', 'image', 'poster', 'cover', 'coverImage', 'thumbnailImage'];
    for (const lt of linkTypes) {
      const link = video._links[lt];
      if (!link) continue;
      if (typeof link === 'string') return link;
      if (link.href) {
        if (link.templated && link.href.includes('{thumbnailWidth}')) return link.href.replace('{thumbnailWidth}', '640');
        return link.href;
      }
    }
  }

  // Alternative links structure
  if (video.links?.thumbnail) {
    const t = typeof video.links.thumbnail === 'string' ? video.links.thumbnail : video.links.thumbnail.href;
    if (t?.includes('{thumbnailWidth}')) return t.replace('{thumbnailWidth}', '640');
    return t;
  }
  if (video.links?.image) {
    return typeof video.links.image === 'string' ? video.links.image : video.links.image.href;
  }

  return null;
};

// Boclips serves HLS streams via playback._links.hlsStream
const getVideoStreamUrl = (video) => {
  if (!video) return null;
  const pbStream = video.playback?._links?.hlsStream;
  if (pbStream?.href) return pbStream.href;
  const linkStream = video._links?.hlsStream;
  if (linkStream?.href) return linkStream.href;
  return null;
};

const getVideoDuration = (video) => {
  if (video.playback?.duration) return formatDuration(video.playback.duration);
  if (video.duration) return formatDuration(video.duration);
  return null;
};

// ─── Main Component ───────────────────────────────────────────────────────────

const Boclips = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const isInitialMount = useRef(true);

  // Core data
  const [currentUser, setCurrentUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Video player modal
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoToken, setVideoToken] = useState(null);

  // UI state
  const [viewMode, setViewMode] = useState('list');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Filters
  const [activeFilters, setActiveFilters] = useState([]);
  const [subjectSearch, setSubjectSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});

  // ─── Auth ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchCurrentUser();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCurrentUser = async () => {
    try {
      // sessionStorage is per-tab; when opened in a new tab we fall back
      // to the cross-tab copy stashed in localStorage by the launcher
      let userDataStr = sessionStorage.getItem('currentUser');
      if (!userDataStr) {
        userDataStr = localStorage.getItem('boclips_currentUser');
        if (userDataStr) {
          sessionStorage.setItem('currentUser', userDataStr);
          localStorage.removeItem('boclips_currentUser');
        }
      }
      if (!userDataStr) {
        setError('Please log in to access Boclips content');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      const userData = JSON.parse(userDataStr);
      setCurrentUser({
        id: userData.id,
        email: userData.email,
        name: userData.name || userData.full_name,
      });
      searchBoclips('', userData.id);
    } catch (err) {
      setError('Failed to load user data');
    }
  };

  // ─── Search ───────────────────────────────────────────────────────────────

  const buildSearchQuery = useCallback(() => {
    let q = searchQuery.trim() || 'education';
    const subjects = activeFilters.filter(f => f.type === 'subject').map(f => f.value);
    const bestFor = activeFilters.filter(f => f.type === 'bestFor').map(f => f.value);
    if (subjects.length) q += ' ' + subjects.join(' ');
    if (bestFor.length) q += ' ' + bestFor.join(' ');
    return q;
  }, [searchQuery, activeFilters]);

  const searchBoclips = async (query = '', userId = null) => {
    if (!userId && !currentUser) {
      setError('User not logged in');
      return;
    }

    const targetUserId = userId || currentUser.id;
    setLoading(true);
    setError(null);

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
      const adminApiKey = process.env.REACT_APP_ADMIN_API_KEY;
      if (!adminApiKey) throw new Error('API key not configured');

      const params = new URLSearchParams({
        userId: targetUserId,
        query: query || 'education',
        limit: '20',
        page: '0',
      });

      const response = await fetch(`${apiBaseUrl}/providers/boclips/search?${params.toString()}`, {
        method: 'GET',
        headers: { 'x-api-key': adminApiKey },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.details || errorData.message || errorData.error || 'Failed to search Boclips');
      }

      const data = await response.json();

      let videoList = [];

      if (data._embedded?.videos && Array.isArray(data._embedded.videos)) {
        videoList = data._embedded.videos;
      } else if (data.page && Array.isArray(data.page)) {
        videoList = data.page;
      } else if (Array.isArray(data)) {
        videoList = data;
      } else if (data.videos && Array.isArray(data.videos)) {
        videoList = data.videos;
      }

      setVideos(videoList);
    } catch (err) {
      setError(err.message || 'Failed to search Boclips content');
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (currentUser) {
      searchBoclips(buildSearchQuery(), currentUser.id);
    } else {
      setError('Please log in to search');
    }
  };

  // Re-search when filters change (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (currentUser) {
      searchBoclips(buildSearchQuery(), currentUser.id);
    }
  }, [activeFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Filter handlers ──────────────────────────────────────────────────────

  const toggleSubject = (subject) => {
    setActiveFilters(prev => {
      const exists = prev.some(f => f.type === 'subject' && f.value === subject);
      return exists
        ? prev.filter(f => !(f.type === 'subject' && f.value === subject))
        : [...prev, { type: 'subject', value: subject }];
    });
  };

  const toggleBestFor = (label) => {
    setActiveFilters(prev => {
      const exists = prev.some(f => f.type === 'bestFor' && f.value === label);
      return exists
        ? prev.filter(f => !(f.type === 'bestFor' && f.value === label))
        : [...prev, { type: 'bestFor', value: label }];
    });
  };

  const removeFilter = (filter) => {
    setActiveFilters(prev => prev.filter(f => !(f.type === filter.type && f.value === filter.value)));
  };

  const clearAllFilters = () => setActiveFilters([]);

  const toggleGroup = (name) => {
    setExpandedGroups(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const filteredCategories = useMemo(() => {
    if (!subjectSearch.trim()) return SUBJECT_CATEGORIES;
    const q = subjectSearch.toLowerCase();
    return SUBJECT_CATEGORIES
      .map(cat => ({
        ...cat,
        subjects: cat.subjects.filter(s => s.toLowerCase().includes(q)),
      }))
      .filter(cat => cat.name.toLowerCase().includes(q) || cat.subjects.length > 0);
  }, [subjectSearch]);

  // ─── Video player ─────────────────────────────────────────────────────────

  const getVideoToken = async (userId) => {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
    const adminApiKey = process.env.REACT_APP_ADMIN_API_KEY;
    if (!adminApiKey) throw new Error('API key not configured');

    const response = await fetch(`${apiBaseUrl}/providers/boclips/token?userId=${userId}`, {
      method: 'GET',
      headers: { 'x-api-key': adminApiKey },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.message || 'Failed to get video token');
    }

    const data = await response.json();
    return data.token;
  };

  const handleVideoClick = async (video) => {
    if (!currentUser) {
      setError('Please log in to watch videos');
      return;
    }
    try {
      setLoading(true);
      const token = await getVideoToken(currentUser.id);
      setVideoToken(token);
      setSelectedVideo(video);
    } catch (err) {
      setError(err.message || 'Failed to open video');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseVideo = () => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    setSelectedVideo(null);
    setVideoToken(null);
  };

  useEffect(() => {
    if (selectedVideo && videoToken && videoRef.current) {
      const streamUrl = getVideoStreamUrl(selectedVideo);
      if (!streamUrl) return;

      if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        videoRef.current.src = streamUrl;
      } else if (Hls.isSupported()) {
        const hls = new Hls();
        hls.loadSource(streamUrl);
        hls.attachMedia(videoRef.current);
        hlsRef.current = hls;

        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data.fatal) {
            if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
              hls.startLoad();
            } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
              hls.recoverMediaError();
            } else {
              hls.destroy();
            }
          }
        });
      } else {
        videoRef.current.src = streamUrl;
      }
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [selectedVideo, videoToken]);

  // ─── Copy link helper ─────────────────────────────────────────────────────

  const handleCopyLink = (e, video) => {
    e.stopPropagation();
    const videoId = video.id || video.videoId;
    if (videoId) {
      navigator.clipboard?.writeText(`${window.location.origin}/boclips/video/${videoId}`);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="bc-page">
      {/* ── Search Section ────────────────────────────────────────── */}
      <div className="bc-search-section">
        <div className="bc-search-row">
          <form onSubmit={handleSearch} className="bc-search-form">
            <div className="bc-search-input-wrap">
              <span className="bc-search-icon"><SearchIcon /></span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for videos..."
                className="bc-search-input"
              />
            </div>
            <button type="submit" className="bc-search-btn" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
          </form>
          <div className="bc-search-logos">
            <img src={boclipsLogo} alt="Boclips" className="bc-search-logo bc-search-logo--boclips" />
            <span className="bc-search-logo-divider" />
            <img src={mdsLogo} alt="MyDigitalSchool" className="bc-search-logo bc-search-logo--mds" />
          </div>
        </div>
      </div>

      {/* ── Tab Navigation ────────────────────────────────────────── */}
      <div className="bc-tabs-bar">
        <div className="bc-tabs-inner">
          {TABS.map(tab => (
            <button key={tab.id} className="bc-tab active">
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Filter Chips ──────────────────────────────────────────── */}
      {activeFilters.length > 0 && (
        <div className="bc-chips-bar">
          <div className="bc-chips-inner">
            {activeFilters.map(filter => (
              <span key={`${filter.type}-${filter.value}`} className={`bc-chip bc-chip--${filter.type}`}>
                {filter.value}
                <button onClick={() => removeFilter(filter)} className="bc-chip-x" aria-label={`Remove ${filter.value} filter`}>
                  <CloseIcon size={10} />
                </button>
              </span>
            ))}
            <button className="bc-clear-all" onClick={clearAllFilters}>Clear all</button>
          </div>
        </div>
      )}

      {/* ── Main Layout ───────────────────────────────────────────── */}
      <div className="bc-layout">
        <>
          <button
              className="bc-sidebar-toggle"
              onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            >
              <FilterIcon />
              <span>Filters</span>
              {activeFilters.length > 0 && (
                <span className="bc-filter-badge">{activeFilters.length}</span>
              )}
            </button>

            <aside className={`bc-sidebar ${mobileSidebarOpen ? 'bc-sidebar--open' : ''}`}>
              <div className="bc-sidebar-head">
                <h3 className="bc-sidebar-title">Filters</h3>
                <button
                  className="bc-sidebar-close-mobile"
                  onClick={() => setMobileSidebarOpen(false)}
                  aria-label="Close filters"
                >
                  <CloseIcon size={16} />
                </button>
              </div>

              {/* Subjects filter */}
              <div className="bc-filter-section">
                <h4 className="bc-filter-heading">Subjects</h4>
                <div className="bc-subject-search-wrap">
                  <span className="bc-subject-search-icon"><SearchIcon /></span>
                  <input
                    type="text"
                    placeholder="Search subjects"
                    className="bc-subject-search"
                    value={subjectSearch}
                    onChange={(e) => setSubjectSearch(e.target.value)}
                  />
                </div>
                <div className="bc-subject-list">
                  {filteredCategories.map(cat => (
                    <div key={cat.name} className="bc-subject-group">
                      <button className="bc-subject-group-btn" onClick={() => toggleGroup(cat.name)}>
                        <span className="bc-subject-group-name">{cat.name}</span>
                        <ChevronIcon expanded={expandedGroups[cat.name]} />
                      </button>
                      {expandedGroups[cat.name] && (
                        <div className="bc-subject-items">
                          {cat.subjects.map(subject => {
                            const checked = activeFilters.some(f => f.type === 'subject' && f.value === subject);
                            return (
                              <label key={subject} className={`bc-check-item ${checked ? 'bc-check-item--active' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleSubject(subject)}
                                  className="bc-check-input"
                                />
                                <span className="bc-check-box" />
                                <span className="bc-check-label">{subject}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                  {filteredCategories.length === 0 && (
                    <p className="bc-no-subjects">No subjects match your search.</p>
                  )}
                </div>
              </div>

              {/* Best for filter */}
              <div className="bc-filter-section">
                <h4 className="bc-filter-heading">Best for</h4>
                <div className="bc-bestfor-list">
                  {BEST_FOR_OPTIONS.map(option => {
                    const checked = activeFilters.some(f => f.type === 'bestFor' && f.value === option.label);
                    return (
                      <label key={option.id} className={`bc-check-item ${checked ? 'bc-check-item--active' : ''}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleBestFor(option.label)}
                          className="bc-check-input"
                        />
                        <span className="bc-check-box" />
                        <span className="bc-check-label">{option.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </aside>
          </>

        {/* ── Content Area ──────────────────────────────────────────── */}
        <main className="bc-content">
              {/* Toolbar */}
              <div className="bc-toolbar">
                <div className="bc-view-toggle">
                  <button
                    className={`bc-view-btn ${viewMode === 'list' ? 'bc-view-btn--active' : ''}`}
                    onClick={() => setViewMode('list')}
                    title="List view"
                  >
                    <ListIcon />
                  </button>
                  <button
                    className={`bc-view-btn ${viewMode === 'grid' ? 'bc-view-btn--active' : ''}`}
                    onClick={() => setViewMode('grid')}
                    title="Grid view"
                  >
                    <GridIcon />
                  </button>
                </div>
              </div>

              {/* Error state */}
              {error && (
                <div className="bc-error">
                  <p>{error}</p>
                </div>
              )}

              {/* Loading state */}
              {loading && (
                <div className="bc-loading">
                  <div className="bc-spinner" />
                  <p>Loading videos...</p>
                </div>
              )}

              {/* Video results */}
              {!loading && videos.length > 0 && (
                <div className={`bc-videos bc-videos--${viewMode}`}>
                  {videos.map(video => {
                    const thumbnail = getVideoThumbnail(video);
                    const duration = getVideoDuration(video);
                    const videoId = video.id || video.videoId || Math.random().toString();
                    const creator = video.createdBy || video.channelName || video.channel?.name || '';
                    const date = formatDate(video.releasedOn || video.publishedAt || video.createdAt);
                    const subjects = (video.subjects || []).map(s => typeof s === 'string' ? s : s.name).filter(Boolean);
                    const ageRange = video.ageRange ? `Ages ${video.ageRange.min}-${video.ageRange.max}` : null;

                    return (
                      <article
                        key={videoId}
                        className="bc-card"
                        onClick={() => handleVideoClick(video)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleVideoClick(video)}
                      >
                        <div className="bc-card-thumb">
                          {thumbnail ? (
                            <img
                              src={thumbnail}
                              alt={video.title || 'Video'}
                              className="bc-card-thumb-img"
                              onError={(e) => { e.target.style.display = 'none'; }}
                              loading="lazy"
                            />
                          ) : null}
                          <div className="bc-card-thumb-overlay">
                            <PlayIcon />
                          </div>
                          {duration && <span className="bc-card-duration">{duration}</span>}
                        </div>

                        <div className="bc-card-body">
                          <h3 className="bc-card-title">{video.title || 'Untitled Video'}</h3>

                          <div className="bc-card-meta">
                            {date && <span className="bc-card-date">{date}</span>}
                            {date && creator && <span className="bc-card-sep">&middot;</span>}
                            {creator && <span className="bc-card-creator">{creator}</span>}
                          </div>

                          {(subjects.length > 0 || ageRange) && (
                            <div className="bc-card-tags">
                              {subjects.slice(0, 3).map(s => (
                                <span key={s} className="bc-tag">{s}</span>
                              ))}
                              {ageRange && <span className="bc-tag bc-tag--age">{ageRange}</span>}
                            </div>
                          )}

                          {viewMode === 'list' && video.description && (
                            <p className="bc-card-desc">
                              {video.description.length > 220
                                ? `${video.description.substring(0, 220)}...`
                                : video.description}
                            </p>
                          )}

                          {viewMode === 'list' && (
                            <div className="bc-card-actions">
                              <button
                                className="bc-action-btn"
                                onClick={(e) => { e.stopPropagation(); }}
                                title="Share video"
                              >
                                <ShareIcon /> Share
                              </button>
                              <button
                                className="bc-action-btn"
                                onClick={(e) => handleCopyLink(e, video)}
                                title="Copy link"
                              >
                                <LinkIcon /> Copy link
                              </button>
                            </div>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}

              {/* Empty state */}
              {!loading && !error && videos.length === 0 && (
                <div className="bc-empty">
                  <div className="bc-empty-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                    </svg>
                  </div>
                  <h3>No videos found</h3>
                  <p>Try adjusting your search or filters to find what you're looking for.</p>
                </div>
              )}
        </main>
      </div>

      {/* ── Video Player Modal ────────────────────────────────────── */}
      {selectedVideo && videoToken && (
        <div className="bc-modal-overlay" onClick={handleCloseVideo}>
          <div className="bc-modal" onClick={(e) => e.stopPropagation()}>
            <button className="bc-modal-close" onClick={handleCloseVideo} aria-label="Close video">
              &times;
            </button>
            <div className="bc-modal-body">
              <h2 className="bc-modal-title">{selectedVideo.title || 'Video'}</h2>
              {selectedVideo.description && (
                <p className="bc-modal-desc">{selectedVideo.description}</p>
              )}
              <video
                ref={videoRef}
                className="bc-video-player"
                controls
                autoPlay
                crossOrigin="anonymous"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Boclips;
